import {Column} from 'platform/components';
import {TextAlign} from 'platform/foundation';
import {useFormatCurrency, useFormatNumber} from 'platform/locale';

import {SalesLeagueLeaderboardResponse} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {SalesLeagueBoardTable} from '../../../components/SalesLeagueBoardTable';
import {SalesLeagueTableHeaderCell} from '../../../components/SalesLeagueTableHeaderCell';
import {SalesLeagueTableRow} from './SalesLeagueTableRow';

const RANK_COLUMN_WIDTH = 12;

interface SalesLeagueTableProps extends RequiredTestIdProps {
  leaderboard: SalesLeagueLeaderboardResponse;
  periodLabel: string;
}

export function SalesLeagueTable(props: SalesLeagueTableProps) {
  const formatCurrency = useFormatCurrency();
  const formatNumber = useFormatNumber();

  const sortedByLabel = props.leaderboard.marginVisible
    ? i18n.t('page.salesLeague.table.sortedBy')
    : i18n.t('page.salesLeague.table.sortedByWithoutMargin');

  const headerCell = (label: string, align: TextAlign, tooltip?: string) => (
    <SalesLeagueTableHeaderCell label={label} align={align} tooltip={tooltip} />
  );

  const columns: Column[] = [
    {
      element: headerCell(i18n.t('page.salesLeague.table.columnRank'), 'left'),
      width: RANK_COLUMN_WIDTH,
    },
    {element: headerCell(i18n.t('page.salesLeague.table.columnRep'), 'left')},
    {
      element: headerCell(
        i18n.t('page.salesLeague.table.columnRankChange'),
        'right',
        i18n.t('page.salesLeague.table.columnRankChangeTooltip')
      ),
    },
    {
      element: headerCell(
        i18n.t('page.salesLeague.table.columnVehiclesChange'),
        'right',
        i18n.t('page.salesLeague.table.columnVehiclesChangeTooltip')
      ),
    },
    {element: headerCell(i18n.t('page.salesLeague.table.columnVehiclesSold'), 'right')},
    ...(props.leaderboard.marginVisible
      ? [
          {element: headerCell(i18n.t('page.salesLeague.table.columnMarginPerVehicle'), 'right')},
          {element: headerCell(i18n.t('page.salesLeague.table.columnTotalMargin'), 'right')},
        ]
      : []),
  ];

  return (
    <SalesLeagueBoardTable
      title={i18n.t('page.salesLeague.table.title')}
      subtitle={sortedByLabel}
      columns={columns}
      rows={props.leaderboard.rows}
      getRowKey={(row) => row.userId}
      renderRow={(row) => (
        <SalesLeagueTableRow
          row={row}
          currency={props.leaderboard.currency}
          marginVisible={props.leaderboard.marginVisible}
          formatCurrency={formatCurrency}
          formatNumber={formatNumber}
          data-testid={suffixTestId(`row-${row.userId}`, props)}
        />
      )}
      footnote={i18n.t('page.salesLeague.table.footnote')}
      periodLabel={props.periodLabel}
      data-testid={props['data-testid']}
    />
  );
}
